import { useEffect, useState } from "react";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { reservationLinks } from "@/components/lib/SidebarLinks";
import { toast } from "react-hot-toast";
import z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import FormInputField from "@/components/lib/from-input-field";
import { useDispatch } from "react-redux";
import {
  addReservation,
  editReservation,
} from "@/redux/slice/reservation-slice/reservation-slice";
import useAuth from "@/hooks/useAuth";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { 
  User, 
  Phone, 
  Mail, 
  Calendar, 
  Clock, 
  Users, 
  CreditCard, 
  FileText, 
  CheckCircle2, 
  Save, 
  Utensils, 
  X, 
  Building2, 
  Smartphone, 
  Globe, 
  HelpCircle,
  Plus,
  Minus,
  CalendarPlus,
  Sparkles,
  Layers
} from "lucide-react";

// Helper to get current UK (Europe/London) Date & Time parts
const getUKDateTimeParts = (date = new Date()) => {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const getPart = (type) => parts.find((p) => p.type === type)?.value || "";
  
  const yyyy = getPart("year");
  const mm = getPart("month");
  const dd = getPart("day");
  const hh = getPart("hour");
  const min = getPart("minute");

  const dateStr = `${yyyy}-${mm}-${dd}`;
  const timeStr = `${hh}:${min}`;

  return { dateStr, timeStr, hours: Number(hh), minutes: Number(min) };
};

// Calculate initial UK start and end times (+1 hour default)
const getInitialUKTimes = () => {
  const now = new Date();
  const { dateStr: ukDateStr, timeStr: ukTimeStr, hours, minutes } = getUKDateTimeParts(now);

  // Round up to nearest 15 mins for clean booking start time
  const roundedMins = Math.ceil(minutes / 15) * 15;
  const startObj = new Date();
  startObj.setHours(hours, roundedMins, 0, 0);
  const endObj = new Date(startObj.getTime() + 60 * 60000); // default 1 hour

  const formatTime = (d) => {
    const h = String(d.getHours()).padStart(2, "0");
    const m = String(d.getMinutes()).padStart(2, "0");
    return `${h}:${m}`;
  };

  return {
    startDate: ukDateStr,
    endDate: ukDateStr,
    startTime: formatTime(startObj),
    endTime: formatTime(endObj),
  };
};

function AddReservation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { type } = useParams();
  const location = useLocation();
  const initialData = location?.state;
  const [callerId, setCallerId] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const initialUK = getInitialUKTimes();

  const form = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      customer_name: "",
      customer_phone: "",
      customer_email: "",
      reservation_type: "in_person",
      start_date: initialUK.startDate,
      end_date: initialUK.endDate,
      start_time: initialUK.startTime,
      end_time: initialUK.endTime,
      guest: 2,
      advance: 0,
      note: "",
    },
  });

  const { user } = useAuth();
  const watchedGuest = Number(form.watch("guest") || 1);
  const watchedAdvance = Number(form.watch("advance") || 0);
  const watchedType = form.watch("reservation_type");
  const watchedStartDate = form.watch("start_date");
  const watchedStartTime = form.watch("start_time");

  useEffect(() => {
    if (type !== "update" && !initialData?.customer) {
      return;
    }

    if (initialData) {
      const getStartTime = () => {
        if (!initialData?.start_date_time)
          return initialData?.start_time?.slice(0, 5) || "";
        if (initialData.start_date_time.includes("T")) {
          return initialData.start_date_time.split("T")[1]?.slice(0, 5) || "";
        }
        return initialData.start_date_time.split(" ")[1]?.slice(0, 5) || "";
      };

      const getEndTime = () => {
        if (!initialData?.end_date_time)
          return initialData?.end_time?.slice(0, 5) || "";
        if (initialData.end_date_time.includes("T")) {
          return initialData.end_date_time.split("T")[1]?.slice(0, 5) || "";
        }
        return initialData.end_date_time.split(" ")[1]?.slice(0, 5) || "";
      };

      const getStartDate = () => {
        if (!initialData?.start_date_time)
          return initialData?.reservation_date?.split("T")[0] || "";
        if (initialData.start_date_time.includes("T")) {
          return initialData.start_date_time.split("T")[0] || "";
        }
        return initialData.start_date_time.split(" ")[0] || "";
      };

      const getEndDate = () => {
        if (!initialData?.end_date_time) return "";
        if (initialData.end_date_time.includes("T")) {
          return initialData.end_date_time.split("T")[0] || "";
        }
        return initialData.end_date_time.split(" ")[0] || "";
      };

      const startDate = getStartDate();
      const endDate = getEndDate();
      const startTime = getStartTime();
      const endTime = getEndTime();
      const customerFullName =
        initialData?.customer?.full_name ||
        [initialData?.customer?.first_name, initialData?.customer?.last_name]
          .filter(Boolean)
          .join(" ")
          .trim();
      
      setCallerId(initialData?.call_log_id || null);

      form.reset({
        customer_name: customerFullName || "",
        customer_phone: initialData?.customer?.number || "",
        customer_email: initialData?.customer?.email || "",
        reservation_type: initialData?.reservation_type || "in_person",
        start_date: startDate || new Date().toISOString().split("T")[0],
        end_date: endDate || startDate || new Date().toISOString().split("T")[0],
        start_time: startTime || "12:00",
        end_time: endTime || "13:00",
        guest: Number(initialData?.guest || 1),
        advance: Number(initialData?.advance || 0),
        note: initialData?.note || "",
      });
    }
  }, [form, initialData, type]);

  // Quick preset helper functions
  const handleDurationAdd = (minutes) => {
    const startTimeStr = form.getValues("start_time");
    const startDateStr = form.getValues("start_date") || new Date().toISOString().split("T")[0];
    if (!startTimeStr) return;

    const [hours, mins] = startTimeStr.split(":").map(Number);
    const startDateObj = new Date(`${startDateStr}T${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:00`);
    
    const endDateObj = new Date(startDateObj.getTime() + minutes * 60000);
    const endHours = String(endDateObj.getHours()).padStart(2, "0");
    const endMins = String(endDateObj.getMinutes()).padStart(2, "0");
    const endDateFormatted = endDateObj.toISOString().split("T")[0];

    form.setValue("end_time", `${endHours}:${endMins}`, { shouldValidate: true });
    form.setValue("end_date", endDateFormatted, { shouldValidate: true });
  };

  const setGuestCount = (val) => {
    const count = Math.max(1, Math.min(500, val));
    form.setValue("guest", count, { shouldValidate: true });
  };

  const setAdvanceAmount = (amount) => {
    form.setValue("advance", amount, { shouldValidate: true });
  };

  const createReservation = async (
    data,
    redirectToAssignTable = false,
    setLoading = null,
  ) => {
    if (callerId) {
      data.call_log_id = callerId;
    }
    const isValid = await form.trigger();
    if (!isValid) {
      console.log("Form Errors:", form.formState.errors);
      return null;
    }

    const payload = {
      ...data,
      restaurant_id: user?.restaurant_id,
      start_date_time: `${data.start_date} ${data.start_time}:00`,
      end_date_time: `${data.end_date || data.start_date} ${data.end_time}:00`,
    };

    try {
      setLoading?.(true);
      let res;
      if (type === "update" && initialData?.id) {
        res = await dispatch(
          editReservation({ reservation_id: initialData.id, data: payload }),
        );
        if (editReservation.fulfilled.match(res)) {
          toast.success(
            data.status === "draft"
              ? "Reservation saved as draft!"
              : "Reservation updated successfully!",
          );
          navigate(
            redirectToAssignTable
              ? `/reservation/details/${initialData.id}`
              : "/reservation",
          );
          form.reset();
        } else if (editReservation.rejected.match(res)) {
          toast.error(res?.payload || "Failed to update reservation!");
        }
      } else {
        res = await dispatch(addReservation(payload));
        if (addReservation.fulfilled.match(res)) {
          setCallerId(null);
          toast.success(
            data.status === "draft"
              ? "Reservation saved as draft!"
              : "Reservation requested successfully!",
          );
          navigate(
            redirectToAssignTable
              ? `/reservation/details/${res.payload.id}`
              : "/reservation",
          );
          form.reset();
        } else if (addReservation.rejected.match(res)) {
          toast.error(res?.payload || "Failed to request reservation!");
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong!");
    } finally {
      setLoading?.(false);
    }
  };

  const bookingTypes = [
    { id: "in_person", label: "In Person", icon: Building2, desc: "Direct walk-in inquiry" },
    { id: "phone", label: "Phone Call", icon: Smartphone, desc: "Booked via telephone" },
    { id: "online", label: "Online", icon: Globe, desc: "Web order booking" },
    { id: "other", label: "Other", icon: HelpCircle, desc: "External / Partner" },
  ];

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={reservationLinks} />}
      pageName={{
        icon: true,
        title: type === "update" ? "Update Reservation" : "Add New Reservation",
      }}
      paths={[
        { name: "Home", path: "/home" },
        {
          name: "Reservation List",
          path: "/reservation",
        },
        {
          name: type === "update" ? "Update Reservation" : "Add New Reservation",
        },
      ]}
    >
      <div className="container flex flex-col gap-6 mt-4 pb-14">
        
        {/* Top Header & Live Glance Strip */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-cblack3 p-5 rounded-xl border border-gray-800 shadow-md">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-cyellow/10 border border-cyellow/20 flex items-center justify-center text-cyellow">
              <CalendarPlus className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h2 className="text-xl font-bold text-white tracking-wide">
                  {type === "update" ? `Update Reservation #${initialData?.id || ""}` : "Create New Reservation"}
                </h2>
                {callerId && (
                  <span className="text-xs px-2 py-0.5 rounded bg-cblue/20 text-cblue border border-cblue/30 font-medium">
                    Call Log #{callerId}
                  </span>
                )}
              </div>
              <p className="text-xs text-cwhite3 mt-1">
                Enter booking details, allocate schedule timing, and configure advance deposit.
              </p>
            </div>
          </div>

          {/* Quick Summary Pill Bar */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-cblack4 px-3 py-1.5 rounded-lg border border-gray-800 text-xs">
              <Users className="h-3.5 w-3.5 text-sky-400" />
              <span className="text-cwhite3 font-medium">Party:</span>
              <span className="text-white font-bold">{watchedGuest} {watchedGuest > 1 ? "Guests" : "Guest"}</span>
            </div>

            <div className="flex items-center gap-2 bg-cblack4 px-3 py-1.5 rounded-lg border border-gray-800 text-xs">
              <CreditCard className="h-3.5 w-3.5 text-cgreen" />
              <span className="text-cwhite3 font-medium">Deposit:</span>
              <span className="text-white font-bold">£{watchedAdvance || "0"}</span>
            </div>

            <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-cyellow/15 text-cyellow border border-cyellow/30 capitalize flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" />
              {watchedType?.replace("_", " ")}
            </span>
          </div>
        </div>

        <Form {...form}>
          <form className="space-y-6">
            
            {/* Main 2-Column Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              
              {/* Left Column (5 Cols): Customer Details & Booking Channel */}
              <div className="lg:col-span-5 flex flex-col gap-6">
                
                {/* Customer Information Card */}
                <Card className="bg-cblack3 border-gray-800 text-white shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-gray-800/80 flex items-center gap-2">
                    <User className="h-4 w-4 text-cyellow" />
                    <h3 className="font-semibold text-white text-base">Customer Details</h3>
                  </div>
                  <CardContent className="p-5 space-y-4">
                    {/* Phone Number */}
                    <div>
                      <FormInputField
                        name="customer_phone"
                        style="bg-cblack4 border border-gray-800 focus:border-cyellow rounded-lg text-white"
                        placeholder="e.g. 01777118524"
                        form={form}
                        type="number"
                        label="Phone Number"
                        required={true}
                      />
                    </div>

                    {/* Customer Full Name */}
                    <div>
                      <FormInputField
                        name="customer_name"
                        style="bg-cblack4 border border-gray-800 focus:border-cyellow rounded-lg text-white"
                        placeholder="e.g. John Doe"
                        form={form}
                        type="text"
                        label="Customer Full Name"
                        required={true}
                      />
                    </div>

                    {/* Email Address */}
                    <div>
                      <FormInputField
                        name="customer_email"
                        style="bg-cblack4 border border-gray-800 focus:border-cyellow rounded-lg text-white"
                        placeholder="e.g. customer@example.com (optional)"
                        form={form}
                        type="text"
                        label="Email Address"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Booking Channel / Reservation Type Card */}
                <Card className="bg-cblack3 border-gray-800 text-white shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-gray-800/80 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-cblue" />
                      <h3 className="font-semibold text-white text-base">Reservation Channel</h3>
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <div className="grid grid-cols-2 gap-3">
                      {bookingTypes.map((channel) => {
                        const Icon = channel.icon;
                        const isSelected = watchedType === channel.id;
                        return (
                          <button
                            key={channel.id}
                            type="button"
                            onClick={() => form.setValue("reservation_type", channel.id, { shouldValidate: true })}
                            className={`p-3 rounded-xl border flex flex-col items-start text-left transition-all relative ${
                              isSelected
                                ? "bg-cyellow/10 border-cyellow text-white shadow-sm shadow-cyellow/5"
                                : "bg-cblack4 border-gray-800 text-cwhite3 hover:border-gray-700 hover:text-white"
                            }`}
                          >
                            <div className="flex items-center justify-between w-full">
                              <div className={`p-2 rounded-lg ${isSelected ? "bg-cyellow/20 text-cyellow" : "bg-cblack7 text-cwhite3"}`}>
                                <Icon className="h-4 w-4" />
                              </div>
                              {isSelected && (
                                <span className="h-2 w-2 rounded-full bg-cyellow animate-pulse" />
                              )}
                            </div>
                            <span className="font-semibold text-xs text-white mt-2.5">
                              {channel.label}
                            </span>
                            <span className="text-[10px] text-cwhite3 mt-0.5 leading-tight line-clamp-1">
                              {channel.desc}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column (7 Cols): Schedule, Party, Deposit & Notes */}
              <div className="lg:col-span-7 flex flex-col gap-6">
                
                {/* Schedule & Timing Card */}
                <Card className="bg-cblack3 border-gray-800 text-white shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-gray-800/80 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-cyellow" />
                      <h3 className="font-semibold text-white text-base">Schedule & Duration</h3>
                    </div>
                    
                    {/* Quick Duration Buttons */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] text-cwhite3 mr-1">Duration:</span>
                      {[
                        { label: "30m", mins: 30 },
                        { label: "1h", mins: 60 },
                        { label: "1.5h", mins: 90 },
                        { label: "2h", mins: 120 },
                      ].map((item) => (
                        <button
                          key={item.label}
                          type="button"
                          onClick={() => handleDurationAdd(item.mins)}
                          className="px-2 py-1 rounded bg-cblack4 hover:bg-cyellow/20 hover:text-cyellow border border-gray-800 text-[11px] font-medium text-cwhite2 transition"
                        >
                          +{item.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <CardContent className="p-5 space-y-4">
                    {/* Start Date & Start Time */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-white flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 text-cyellow" />
                          Reservation Start Date <span className="text-cred">*</span>
                        </label>
                        <input
                          type="date"
                          style={{ colorScheme: "dark" }}
                          className="w-full bg-cblack4 border border-gray-800 focus:border-cyellow rounded-lg px-3 py-2 text-sm text-white transition focus:outline-none"
                          {...form.register("start_date")}
                          onChange={(e) => {
                            form.setValue("start_date", e.target.value);
                            // Auto-set end date to match start date if empty or before
                            if (!form.getValues("end_date")) {
                              form.setValue("end_date", e.target.value);
                            }
                          }}
                        />
                        {form.formState.errors.start_date && (
                          <p className="text-cred text-xs">
                            {form.formState.errors.start_date.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <FormInputField
                          name="start_time"
                          style="bg-cblack4 border border-gray-800 focus:border-cyellow rounded-lg text-white"
                          placeholder="Select Start Time"
                          form={form}
                          type="time"
                          timeFormat="HH:mm"
                          label="Start Time"
                          required={true}
                        />
                      </div>
                    </div>

                    {/* End Date & End Time */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-white flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 text-cpurple" />
                          Reservation End Date
                        </label>
                        <input
                          type="date"
                          style={{ colorScheme: "dark" }}
                          className="w-full bg-cblack4 border border-gray-800 focus:border-cyellow rounded-lg px-3 py-2 text-sm text-white transition focus:outline-none"
                          {...form.register("end_date")}
                        />
                        {form.formState.errors.end_date && (
                          <p className="text-cred text-xs">
                            {form.formState.errors.end_date.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <FormInputField
                          name="end_time"
                          style="bg-cblack4 border border-gray-800 focus:border-cyellow rounded-lg text-white"
                          placeholder="Select End Time"
                          form={form}
                          type="time"
                          timeFormat="HH:mm"
                          label="End Time"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Party Size & Advance Deposit Card */}
                <Card className="bg-cblack3 border-gray-800 text-white shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-gray-800/80 flex items-center gap-2">
                    <Users className="h-4 w-4 text-sky-400" />
                    <h3 className="font-semibold text-white text-base">Party Size & Deposit</h3>
                  </div>

                  <CardContent className="p-5 space-y-5">
                    {/* Number of Guests */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-white flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5 text-sky-400" />
                          Number of Guests <span className="text-cred">*</span>
                        </label>
                        <span className="text-xs font-bold text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded border border-sky-500/20">
                          {watchedGuest} {watchedGuest > 1 ? "Persons" : "Person"}
                        </span>
                      </div>

                      {/* Stepper + Preset Chips */}
                      <div className="flex items-center gap-3">
                        <div className="flex items-center bg-cblack4 border border-gray-800 rounded-lg p-1">
                          <button
                            type="button"
                            onClick={() => setGuestCount(watchedGuest - 1)}
                            className="h-8 w-8 rounded-md bg-cblack7 hover:bg-gray-700 flex items-center justify-center text-white transition"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <input
                            type="number"
                            className="w-14 text-center bg-transparent text-white font-bold text-sm focus:outline-none"
                            {...form.register("guest")}
                          />
                          <button
                            type="button"
                            onClick={() => setGuestCount(watchedGuest + 1)}
                            className="h-8 w-8 rounded-md bg-cblack7 hover:bg-gray-700 flex items-center justify-center text-white transition"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>

                        {/* Quick Presets */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {[2, 4, 6, 8, 10].map((preset) => (
                            <button
                              key={preset}
                              type="button"
                              onClick={() => setGuestCount(preset)}
                              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition ${
                                watchedGuest === preset
                                  ? "bg-sky-500/20 border-sky-500 text-sky-400"
                                  : "bg-cblack4 border-gray-800 text-cwhite3 hover:border-gray-700 hover:text-white"
                              }`}
                            >
                              {preset}p
                            </button>
                          ))}
                        </div>
                      </div>
                      {form.formState.errors.guest && (
                        <p className="text-cred text-xs">
                          {form.formState.errors.guest.message}
                        </p>
                      )}
                    </div>

                    {/* Advance Deposit */}
                    <div className="space-y-2 pt-2 border-t border-gray-800/60">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-white flex items-center gap-1.5">
                          <CreditCard className="h-3.5 w-3.5 text-cgreen" />
                          Advance Deposit Amount (£)
                        </label>
                        <span className="text-xs font-bold text-cgreen bg-cgreen/10 px-2 py-0.5 rounded border border-cgreen/20">
                          £{watchedAdvance.toFixed(2)}
                        </span>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="relative flex-1 max-w-[200px]">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-cwhite3 font-bold text-sm">
                            £
                          </span>
                          <input
                            type="number"
                            step="0.5"
                            placeholder="0.00"
                            className="w-full pl-8 pr-3 py-2 bg-cblack4 border border-gray-800 focus:border-cyellow rounded-lg text-sm text-white font-bold focus:outline-none transition"
                            {...form.register("advance")}
                          />
                        </div>

                        {/* Quick Deposit Chips */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {[0, 10, 20, 50, 100].map((amt) => (
                            <button
                              key={amt}
                              type="button"
                              onClick={() => setAdvanceAmount(amt)}
                              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition ${
                                watchedAdvance === amt
                                  ? "bg-cgreen/20 border-cgreen text-cgreen"
                                  : "bg-cblack4 border-gray-800 text-cwhite3 hover:border-gray-700 hover:text-white"
                              }`}
                            >
                              {amt === 0 ? "No Deposit" : `£${amt}`}
                            </button>
                          ))}
                        </div>
                      </div>
                      {form.formState.errors.advance && (
                        <p className="text-cred text-xs">
                          {form.formState.errors.advance.message}
                        </p>
                      )}
                    </div>

                    {/* Special Instructions & Note */}
                    <div className="space-y-1.5 pt-2 border-t border-gray-800/60">
                      <label className="text-xs font-semibold text-white flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5 text-cyellow" />
                        Special Instructions / Staff Notes
                      </label>
                      <textarea
                        rows={2}
                        placeholder="e.g. Birthday decoration, window seat requested, allergic to peanuts..."
                        className="w-full bg-cblack4 border border-gray-800 focus:border-cyellow rounded-lg p-3 text-xs text-white placeholder-cwhite3 transition focus:outline-none resize-none"
                        {...form.register("note")}
                      />
                      {form.formState.errors.note && (
                        <p className="text-cred text-xs">
                          {form.formState.errors.note.message}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Bottom Action Controls Dock */}
            <div className="flex items-center gap-3 flex-wrap p-4 bg-cblack3 rounded-xl border border-gray-800 shadow-md">
              {/* Cancel Button */}
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.reset();
                  navigate(-1);
                }}
                className="bg-cblack4 hover:bg-cblack7 border-gray-800 text-cwhite3 hover:text-white flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </Button>

              <div className="flex items-center gap-3 ml-auto flex-wrap">
                {/* Save as Draft Button */}
                <Button
                  type="button"
                  disabled={draftLoading || assignLoading || confirmLoading}
                  isLoading={draftLoading}
                  onClick={() =>
                    form.handleSubmit((data) =>
                      createReservation(
                        { ...data, status: "draft" },
                        false,
                        setDraftLoading,
                      ),
                    )()
                  }
                  className="bg-cyellow hover:bg-cyellow/90 text-black font-semibold flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save as Draft
                </Button>

                {/* Confirm & Assign Table Button */}
                <Button
                  type="button"
                  disabled={draftLoading || assignLoading || confirmLoading}
                  isLoading={assignLoading}
                  onClick={form.handleSubmit((data) =>
                    createReservation({ ...data, status: "confirmed" }, true, setAssignLoading),
                  )}
                  className="bg-sky-500 hover:bg-sky-600 text-white font-semibold flex items-center gap-2 shadow"
                >
                  <Utensils className="w-4 h-4" />
                  Confirm & Assign Table
                </Button>

                {/* Confirm / Confirm & Update Button */}
                <Button
                  type="button"
                  disabled={draftLoading || assignLoading || confirmLoading}
                  isLoading={confirmLoading}
                  onClick={form.handleSubmit((data) =>
                    createReservation({ ...data, status: "confirmed" }, false, setConfirmLoading),
                  )}
                  className="bg-cgreen hover:bg-cgreen/90 text-white font-bold flex items-center gap-2 shadow-md"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {type === "update" ? "Confirm & Update" : "Confirm"}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default AddReservation;

const schema = z
  .object({
    customer_name: z
      .string()
      .min(2, { message: "Customer name must be at least 2 characters" })
      .max(50, { message: "Customer name can't exceed 50 characters" }),

    customer_phone: z
      .string()
      .min(11, { message: "Phone number is too short" })
      .max(11, { message: "Phone number is too long" })
      .regex(/^\+?\d+$/, {
        message: "Enter a valid phone number (digits only, with or without +)",
      }),

    customer_email: z
      .string()
      .email({ message: "Enter a valid email address" })
      .or(z.literal(""))
      .optional(),

    reservation_type: z.enum(["in_person", "online", "other", "phone"], {
      required_error: "Reservation type is required",
    }),

    start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
      message: "Date is required",
    }),
    end_date: z
      .string()
      .optional()
      .refine((val) => !val || /^\d{4}-\d{2}-\d{2}$/.test(val), {
        message: "Date must be in YYYY-MM-DD format",
      }),

    start_time: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
      message: "Start time is required",
    }),

    end_time: z
      .string()
      .optional()
      .refine((val) => !val || /^([01]\d|2[0-3]):([0-5]\d)$/.test(val), {
        message: "End time must be in HH:MM format (24-hour)",
      }),

    guest: z
      .union([z.string(), z.number()])
      .transform((val) => Number(val))
      .refine((val) => !isNaN(val), { message: "Guest must be a number" })
      .refine((val) => val >= 1, { message: "At least 1 guest required" })
      .refine((val) => val <= 500, {
        message: "Guest limit exceeded (max 500)",
      }),

    advance: z
      .union([z.string(), z.number()])
      .optional()
      .transform((val) => (val === "" || val === undefined ? 0 : Number(val)))
      .refine((val) => !isNaN(val), { message: "Advance must be a number" })
      .refine((val) => val >= 0, { message: "Advance can't be negative" })
      .refine((val) => val <= 1000000, { message: "Advance too large" }),

    note: z
      .string()
      .max(200, { message: "Note can't exceed 200 characters" })
      .optional(),
  })
  .refine(
    (data) => {
      if (
        !data.start_date ||
        !data.start_time ||
        !data.end_date ||
        !data.end_time
      )
        return true;
      const start = new Date(`${data.start_date}T${data.start_time}:00`);
      const end = new Date(`${data.end_date}T${data.end_time}:00`);
      return end > start;
    },
    {
      message: "End date and time must be after start date and time",
      path: ["end_time"],
    },
  );
